import axios from 'axios';
import { api } from '@/services/api';
import { getAccessToken } from '@/services/token';
import { ApiResponse } from '@/types/api';
import { ArticleBasicInfo, ArticleCreateResponse, ArticleCreateInfo, BulkArticleRow, BulkArticleCreateResponse, CompetitorHeading, HeadingSubmitData, ContentItem, Heading, ArticleVersionSummary, ArticleVersionContent } from '@/types/article';
import { ERROR_MESSAGES } from '@/types/error';
import { handleError } from '@/services/error';
import { HeadingPolicy, ImagePolicy, Audience } from '@/types/admin';

// 新規記事の基本設定を作成する関数
export const createArticleBasicSettings = async (formData: ArticleCreateInfo): Promise<ArticleCreateResponse> => {
  try {
    const response = await api.post('/article/basic-settings/',
      formData,
      {
        headers: {
          'Authorization': `Bearer ${getAccessToken()}`,
          'Content-Type': 'application/json'
        }
      }
    );
    return response.data;
  } catch (error) {
    return handleError(error, 'VALIDATION_ERROR');
  }
};

// 記事の基本設定を取得する関数
export const getArticleBasicSettings = async (articleId: string): Promise<ApiResponse<ArticleCreateInfo>> => {
  try {
    const response = await api.get(`/article/basic-settings/${articleId}/`, {
      headers: {
        'Authorization': `Bearer ${getAccessToken()}`,
      }
    });
    return {
      success: true,
      data: response.data
    };
  } catch (error) {
    return handleError<ArticleCreateInfo>(error, 'FETCH_ERROR');
  }
};

// 記事の基本設定を更新する関数
export const updateArticleBasicSettings = async (
  articleId: string,
  formData: Partial<ArticleCreateInfo>
): Promise<ApiResponse> => {
  try {
    const response = await api.patch(
      `/article/basic-settings/${articleId}/`,
      formData,
      {
        headers: {
          'Authorization': `Bearer ${getAccessToken()}`,
          'Content-Type': 'application/json'
        }
      }
    );
    return {
      success: true,
      data: response.data
    };
  } catch (error) {
    return handleError(error, 'UPDATE_ERROR');
  }
};

// 見出しを作成する関数
export const createArticleHeading = async (
  headings: HeadingSubmitData[],
  articleId: number
): Promise<ApiResponse> => {
  try {
    const response = await api.post(
      `/article/article-heading/`,
      {
        headings,
        article_id: articleId
      },
      {
        headers: {
          'Authorization': `Bearer ${getAccessToken()}`,
          'Content-Type': 'application/json'
        }
      }
    );
    return {
      success: true,
      data: response.data
    };
  } catch {
    return {
      success: false,
      message: ERROR_MESSAGES['HEADING_CREATE_ERROR']
    };
  }
};

// SEOキーワードの存在チェック
export const checkSeoKeywordExists = async (seoKeyword: string): Promise<boolean> => {
  try {
    const response = await api.post('/article/check-seo-keyword/', { seo_keyword: seoKeyword }, {
      headers: {
        Authorization: `Bearer ${getAccessToken()}`,
      },
    });
    return response.data.exists;
  } catch {
    return false;
  }
};

// 記事一覧・表示関連 //////////////////////////////////////////////////////////////////////
// 記事一覧の取得
export const getArticles = async (): Promise<ApiResponse> => {
  try {
    const response = await api.get('/articles/');
    return { success: true, data: response.data };
  } catch (error) {
    if (axios.isAxiosError(error) && error.response) {
      return handleError(error, 'REQUEST_FAILED');
    }
    return handleError(error, 'UNKNOWN_ERROR');
  }
};

// 完了した記事数の取得
export const getCompletedArticlesCount = async (): Promise<ApiResponse<number>> => {
  try {
    const response = await api.get('/articles/completed-count/');
    return {
      success: true,
      data: response.data['完了済み記事数']  // 日本語のキーを使用
    };
  } catch (error) {
    return handleError<number>(error, 'REQUEST_FAILED');
  }
};

// 総文字数の取得
export const getTotalCharacters = async (): Promise<ApiResponse<number>> => {
  try {
    const response = await api.get('/article/total-characters/');
    return { success: true, data: response.data.total_characters };
  } catch (error) {
    return handleError<number>(error, 'REQUEST_FAILED');
  }
};

// 記事タイトルの取得
export const getArticleTitle = async (articleId: string): Promise<ApiResponse<{ title: string }>> => {
  try {
    const response = await api.get(`/article/title/${articleId}/`);
    return { success: true, data: response.data };
  } catch (error) {
    if (axios.isAxiosError(error) && error.response) {
      return handleError<{ title: string }>(error, 'REQUEST_FAILED');
    }
    return handleError<{ title: string }>(error, 'UNKNOWN_ERROR');
  }
};

// 記事の見出しを取得する関数
export const getArticleHeadings = async (articleId: string): Promise<ApiResponse<Heading[]>> => {
  try {
    const response = await api.get(`/article/headings/${articleId}/`);
    return { success: true, data: response.data };
  } catch (error) {
    if (axios.isAxiosError(error) && error.response) {
      return handleError<Heading[]>(error, 'REQUEST_FAILED');
    }
    return handleError<Heading[]>(error, 'UNKNOWN_ERROR');
  }
};

// リード文の取得
export const getArticleReadContent = async (articleId: string): Promise<ApiResponse<string>> => {
  try {
    const response = await api.get(`/article/read-content/${articleId}/`);
    return { success: true, data: response.data.content };
  } catch (error) {
    if (axios.isAxiosError(error) && error.response) {
      return handleError<string>(error, 'REQUEST_FAILED');
    }
    return handleError<string>(error, 'UNKNOWN_ERROR');
  }
};

// 記事コンテンツの取得
export const getArticleContent = async (articleId: string): Promise<ApiResponse<ContentItem[]>> => {
  try {
    const response = await api.get(`/article/content/${articleId}/`);
    return { success: true, data: response.data };
  } catch (error) {
    if (axios.isAxiosError(error) && error.response) {
      return handleError<ContentItem[]>(error, 'REQUEST_FAILED');
    }
    return handleError<ContentItem[]>(error, 'UNKNOWN_ERROR');
  }
};

// 記事画像の存在確認
export const checkArticleImageExists = async (articleId: string, headingId: string): Promise<ApiResponse<{ exists: boolean }>> => {
  try {
    const response = await api.get(`/article/images/${articleId}/${headingId}/exists/`);
    return { success: true, data: response.data };
  } catch (error) {
    return handleError<{ exists: boolean }>(error, 'REQUEST_FAILED');
  }
};

// 記事画像の取得
export const getArticleImage = async (articleId: string, headingId: string): Promise<ApiResponse<Blob>> => {
  try {
    const response = await api.get(`/article/images/${articleId}/${headingId}/`, {
      responseType: 'blob'
    });

    // 404エラーの場合（interceptorからエラー情報付きレスポンスが返される）
    if (response.status === 404 || (response as { error?: string }).error === 'NOT_FOUND') {
      return { success: false, message: 'NOT_FOUND' };
    }

    return { success: true, data: response.data };
  } catch (error) {
    return handleError<Blob>(error, 'REQUEST_FAILED');
  }
};

// 記事の更新
export const updateArticleContent = async (
  articleId: string,
  data: {
    title?: string;
    read_content?: string;
    contents?: ContentItem | ContentItem[];
  }
): Promise<ApiResponse<ContentItem>> => {
  try {
    const response = await api.put(`/articles/update/${articleId}/`, data);
    return { success: true, data: response.data };
  } catch (error) {
    if (axios.isAxiosError(error) && error.response) {
      return handleError<ContentItem>(error, 'VALIDATION_ERROR');
    }
    return handleError<ContentItem>(error, 'UNKNOWN_ERROR');
  }
};

// 記事のエクスポート
export const exportArticleToWord = async (
  articleId: string,
  options?: { includeRegulationComments?: boolean; includeFactCheckComments?: boolean }
): Promise<{ blob: Blob; filename: string }> => {
  try {
    const token = getAccessToken();
    if (!token) {
      throw new Error('認証トークンがありません');
    }

    // クエリパラメータを構築
    const params = new URLSearchParams();
    if (options?.includeRegulationComments) {
      params.append('include_regulation_comments', 'true');
    }
    if (options?.includeFactCheckComments) {
      params.append('include_fact_check_comments', 'true');
    }
    const queryString = params.toString();
    const url = `${process.env.NEXT_PUBLIC_API_URL}/articles/export/${articleId}/${queryString ? `?${queryString}` : ''}`;

    const response = await fetch(url, {
      method: 'GET',
      headers: {
        'Authorization': `Bearer ${token}`,
      },
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.error || 'エクスポートに失敗しました');
    }

    // Content-Dispositionヘッダーからファイル名を取得
    const contentDisposition = response.headers.get('content-disposition');

    // Base64エンコードされたContent-Dispositionヘッダーをデコード
    const decodedDisposition = contentDisposition
      ?.replace(/=\?utf-8\?b\?(.*?)\?=/gi, (match, p1) => {
        try {
          return atob(p1);
        } catch {
          return match;
        }
      });

    // filename*=UTF-8''の部分を探す
    const filenameMatch = decodedDisposition?.match(/filename\*=UTF-8''([^;]+)/i);
    // 通常のfilenameの部分を探す（バックアップ）
    const fallbackMatch = decodedDisposition?.match(/filename="([^"]+)"/);

    let filename;
    if (filenameMatch?.[1]) {
      // UTF-8エンコードされたファイル名を使用
      filename = decodeURIComponent(filenameMatch[1]);
    } else if (fallbackMatch?.[1]) {
      // 通常のファイル名を使用
      filename = fallbackMatch[1];
    } else {
      // どちらも見つからない場合はデフォルト名を使用
      filename = `article_${articleId}.docx`;
    }

    const blob = await response.blob();
    return { blob, filename };
  } catch (error) {
    console.error('Export error:', error);
    throw error;
  }
};

// 記事のMarkdownエクスポート
export const exportArticleToMarkdown = async (articleId: string): Promise<{ blob: Blob; filename: string }> => {
  try {
    const token = getAccessToken();
    if (!token) {
      throw new Error('認証トークンがありません');
    }

    const response = await fetch(`${process.env.NEXT_PUBLIC_API_URL}/articles/export-markdown/${articleId}/`, {
      method: 'GET',
      headers: {
        'Authorization': `Bearer ${token}`,
      },
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.error || 'エクスポートに失敗しました');
    }

    // Content-Dispositionヘッダーからファイル名を取得
    const contentDisposition = response.headers.get('content-disposition');
    const decodedDisposition = contentDisposition
      ?.replace(/=\?utf-8\?b\?(.*?)\?=/gi, (match, p1) => {
        try {
          return atob(p1);
        } catch {
          return match;
        }
      });

    const filenameMatch = decodedDisposition?.match(/filename\*=UTF-8''([^;]+)/i);
    const fallbackMatch = decodedDisposition?.match(/filename="([^"]+)"/);

    let filename;
    if (filenameMatch?.[1]) {
      filename = decodeURIComponent(filenameMatch[1]);
    } else if (fallbackMatch?.[1]) {
      filename = fallbackMatch[1];
    } else {
      filename = `article_${articleId}.zip`;
    }

    const blob = await response.blob();
    return { blob, filename };
  } catch (error) {
    console.error('Export error:', error);
    throw error;
  }
};

// 記事のHTMLエクスポート
export const exportArticleToHtml = async (articleId: string): Promise<{ blob: Blob; filename: string }> => {
  try {
    const token = getAccessToken();
    if (!token) {
      throw new Error('認証トークンがありません');
    }

    console.log(`HTMLエクスポート開始: 記事ID=${articleId}`);
    const response = await fetch(`${process.env.NEXT_PUBLIC_API_URL}/articles/export-html/${articleId}/`, {
      method: 'GET',
      headers: {
        'Authorization': `Bearer ${token}`,
      },
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.error || 'エクスポートに失敗しました');
    }

    // Content-Dispositionヘッダーからファイル名を取得
    const contentDisposition = response.headers.get('content-disposition');
    console.log(`Content-Disposition: ${contentDisposition}`);
    const decodedDisposition = contentDisposition
      ?.replace(/=\?utf-8\?b\?(.*?)\?=/gi, (match, p1) => {
        try {
          return atob(p1);
        } catch {
          return match;
        }
      });

    // Markdownエクスポートと同様のファイル名抽出ロジック
    const filenameMatch = decodedDisposition?.match(/filename\*=UTF-8''([^;]+)/i);
    const fallbackMatch = decodedDisposition?.match(/filename="([^"]+)"/);

    let filename;
    if (filenameMatch?.[1]) {
      filename = decodeURIComponent(filenameMatch[1]);
      console.log(`filename*=形式から取得したファイル名: ${filename}`);
    } else if (fallbackMatch?.[1]) {
      filename = fallbackMatch[1];
      console.log(`filename=形式から取得したファイル名: ${filename}`);
    } else {
      filename = `article_${articleId}.zip`;
      console.log(`ファイル名が取得できなかったため、デフォルト値を使用: ${filename}`);
    }

    const blob = await response.blob();
    console.log(`ダウンロードしたファイルサイズ: ${blob.size} バイト`);
    return { blob, filename };
  } catch (error) {
    console.error('Export error:', error);
    throw error;
  }
};

// 見出し設計方針一覧の取得
export const getHeadingPolicies = async (): Promise<ApiResponse<HeadingPolicy[]>> => {
  try {
    const response = await api.get('/heading-policies/');

    if (response.data && Array.isArray(response.data)) {
      return { success: true, data: response.data };
    } else {
      return {
        success: false,
        message: 'データの形式が不正です',
        data: []
      };
    }
  } catch (error) {
    return handleError<HeadingPolicy[]>(error, 'REQUEST_FAILED');
  }
};

// 見出し設計方針の取得
export const getHeadingPolicy = async (policyId: string): Promise<ApiResponse<HeadingPolicy>> => {
  try {
    const response = await api.get(`/heading-policies/${policyId}/`);
    return { success: true, data: response.data };
  } catch (error) {
    if (axios.isAxiosError(error) && error.response) {
      return handleError<HeadingPolicy>(error, 'REQUEST_FAILED');
    }
    return handleError<HeadingPolicy>(error, 'UNKNOWN_ERROR');
  }
};

// 見出し設計方針の作成
export const createHeadingPolicy = async (policyData: {
  policy_name: string;
  prompt: string;
}): Promise<ApiResponse> => {
  try {
    const response = await api.post('/heading-policies/', policyData, {
      headers: {
        'Authorization': `Bearer ${getAccessToken()}`,
        'Content-Type': 'application/json',
      },
    });
    if (response.data.success) {
      return { success: true, data: response.data.data };
    } else {
      return handleError(null, 'VALIDATION_ERROR');
    }
  } catch (error) {
    if (axios.isAxiosError(error) && error.response) {
      return handleError(error, 'VALIDATION_ERROR');
    }
    return handleError(error, 'UNKNOWN_ERROR');
  }
};

// 見出し設計方針の更新
export const updateHeadingPolicy = async (policyId: string, policyData: {
  policy_name: string;
  prompt: string;
  status?: boolean;
}): Promise<ApiResponse> => {
  try {
    const response = await api.put(`/heading-policies/${policyId}/`, policyData, {
      headers: {
        'Authorization': `Bearer ${getAccessToken()}`
      }
    });
    return { success: true, data: response.data };
  } catch (error) {
    if (axios.isAxiosError(error) && error.response) {
      return handleError(error, 'VALIDATION_ERROR');
    }
    return handleError(error, 'UNKNOWN_ERROR');
  }
};

// 見出しの更新
export const updateArticleHeadings = async (articleId: string, headings: Array<Partial<Heading>>): Promise<ApiResponse> => {
  try {
    // タイムアウトを30秒に延長（見出し保存処理が時間がかかるため）
    const response = await api.put(
      `/article/headings/${articleId}/update/`,
      { headings },
      { timeout: 30000 }
    );
    return { success: true, data: response.data };
  } catch (error) {
    if (axios.isAxiosError(error) && error.response) {
      return handleError(error, 'VALIDATION_ERROR');
    }
    return handleError(error, 'UNKNOWN_ERROR');
  }
};

export const processArticleHeadingCheck = async (articleId: number): Promise<ApiResponse> => {
  try {
    const response = await api.post(`/process-article/${articleId}/heading-check/`, {}, {
      headers: {
        'Authorization': `Bearer ${getAccessToken()}`
      }
    });
    return { success: true, message: response.data.message };
  } catch (error) {
    if (axios.isAxiosError(error) && error.response) {
      return handleError(error, 'REQUEST_FAILED');
    }
    return handleError(error, 'UNKNOWN_ERROR');
  }
};

export const getArticleStatus = async (id: string): Promise<ApiResponse<{ status: string }>> => {
  try {
    const response = await api.get(`/articles/${id}/status`, {
      headers: {
        'Authorization': `Bearer ${getAccessToken()}`
      }
    });
    return { success: true, data: response.data };
  } catch (error) {
    if (axios.isAxiosError(error) && error.response) {
      return handleError<{ status: string }>(error, 'REQUEST_FAILED');
    }
    return handleError<{ status: string }>(error, 'UNKNOWN_ERROR');
  }
};

// 見出し作成後のプロセスを実行する関数
export const processArticlePostHeadingCreation = async (articleId: string): Promise<ApiResponse> => {
  try {
    const response = await api.post(`/article/${articleId}/post-heading-creation/`, {}, {
      headers: {
        'Authorization': `Bearer ${getAccessToken()}`
      }
    });
    return { success: true, message: response.data.message };
  } catch (error) {
    if (axios.isAxiosError(error) && error.response) {
      return handleError(error, 'REQUEST_FAILED');
    }
    return handleError(error, 'UNKNOWN_ERROR');
  }
};

// 読者レベル一覧の取得
export const getTargetAudiences = async (): Promise<ApiResponse<Audience[]>> => {
  try {
    const response = await api.get('/target-audiences/', {
      headers: {
        'Authorization': `Bearer ${getAccessToken()}`
      }
    });
    return { success: true, data: response.data };
  } catch (error) {
    return handleError<Audience[]>(error, 'REQUEST_FAILED');
  }
};

// 読者レベルの更新
export const updateTargetAudience = async (audienceId: number, audienceData: {
  audience_name?: string;
  persona_prompt?: string;
  heading_prompt?: string;
  article_prompt?: string;
  status?: boolean;
}): Promise<ApiResponse<Audience>> => {
  try {
    const response = await api.patch(`/target-audiences/${audienceId}/`, audienceData, {
      headers: {
        Authorization: `Bearer ${getAccessToken()}`
      },
    });
    return { success: true, data: response.data };
  } catch (error) {
    return handleError<Audience>(error, 'REQUEST_FAILED');
  }
};

// 読者レベルの作成
export const createTargetAudience = async (audienceData: {
  audience_name: string;
  persona_prompt: string;
  heading_prompt: string;
  article_prompt: string;
}): Promise<ApiResponse<Audience>> => {
  try {
    const response = await api.post('/target-audiences/', audienceData, {
      headers: {
        Authorization: `Bearer ${getAccessToken()}`
      },
    });
    return { success: true, data: response.data };
  } catch (error) {
    return handleError<Audience>(error, 'REQUEST_FAILED');
  }
};

// 読者レベルの削除
export const deleteTargetAudience = async (audienceId: number): Promise<ApiResponse> => {
  try {
    await api.delete(`/target-audiences/${audienceId}/`, {
      headers: {
        Authorization: `Bearer ${getAccessToken()}`
      },
    });
    return { success: true };
  } catch (error) {
    return handleError(error, 'REQUEST_FAILED');
  }
};

// 記事の画像生成フラグを取得
export const getArticleImageGeneration = async (articleId: string): Promise<ApiResponse<boolean>> => {
  try {
    const response = await api.get(`/article/image-generation/${articleId}/`);
    return { success: true, data: response.data.is_image_generation };
  } catch (error) {
    if (axios.isAxiosError(error) && error.response) {
      return handleError<boolean>(error, 'REQUEST_FAILED');
    }
    return handleError<boolean>(error, 'UNKNOWN_ERROR');
  }
};

// 画像ポリシー一覧の取得
export const getImagePolicies = async (queryParams: string = ''): Promise<ApiResponse<ImagePolicy[]>> => {
  try {
    const response = await api.get(`/image-policies/${queryParams}`);

    if (response.data && Array.isArray(response.data)) {
      return { success: true, data: response.data };
    } else {
      return {
        success: false,
        message: 'データの形式が不正です',
        data: []
      };
    }
  } catch (error) {
    return handleError<ImagePolicy[]>(error, 'REQUEST_FAILED');
  }
};

// 画像ポリシーの取得
export const getImagePolicy = async (policyId: string): Promise<ApiResponse<ImagePolicy>> => {
  try {
    const response = await api.get(`/image-policies/${policyId}/`);
    return { success: true, data: response.data };
  } catch (error) {
    if (axios.isAxiosError(error) && error.response) {
      return handleError<ImagePolicy>(error, 'REQUEST_FAILED');
    }
    return handleError<ImagePolicy>(error, 'UNKNOWN_ERROR');
  }
};

// 画像ポリシーの作成
export const createImagePolicy = async (policyData: {
  image_name: string;
  image_prompt: string;
  is_thumbnail_style?: boolean;
  is_overlay_image?: boolean;
}): Promise<ApiResponse> => {
  try {
    const response = await api.post('/image-policies/', policyData, {
      headers: {
        'Authorization': `Bearer ${getAccessToken()}`,
        'Content-Type': 'application/json',
      },
    });
    return { success: true, data: response.data };
  } catch (error) {
    if (axios.isAxiosError(error) && error.response) {
      return handleError(error, 'VALIDATION_ERROR');
    }
    return handleError(error, 'UNKNOWN_ERROR');
  }
};

// 画像ポリシーの更新
export const updateImagePolicy = async (policyId: string, policyData: {
  image_name: string;
  image_prompt: string;
  status?: boolean;
  is_thumbnail_style?: boolean;
  is_overlay_image?: boolean;
}): Promise<ApiResponse> => {
  try {
    const response = await api.put(`/image-policies/${policyId}/`, policyData, {
      headers: {
        'Authorization': `Bearer ${getAccessToken()}`
      }
    });
    return { success: true, data: response.data };
  } catch (error) {
    if (axios.isAxiosError(error) && error.response) {
      return handleError(error, 'VALIDATION_ERROR');
    }
    return handleError(error, 'UNKNOWN_ERROR');
  }
};

export const getCompetitorHeadings = async (articleId: string, searchRank: number): Promise<ApiResponse<CompetitorHeading[]>> => {
  try {
    const response = await api.get(
      `/articles/${articleId}/competitor-headings/?search_rank=${searchRank}`,
      {
        headers: {
          'Authorization': `Bearer ${getAccessToken()}`
        }
      }
    );
    return { success: true, data: response.data };
  } catch (error) {
    console.error('Error fetching competitor headings:', error);
    if (axios.isAxiosError(error) && error.response) {
      return handleError<CompetitorHeading[]>(error, 'REQUEST_FAILED');
    }
    return handleError<CompetitorHeading[]>(error, 'UNKNOWN_ERROR');
  }
};

// 見出しのエクスポート（記事基本情報付き）
export const exportArticleHeadingsWithInfoToWord = async (articleId: string): Promise<{ blob: Blob; filename: string }> => {
  try {
    const token = getAccessToken();
    if (!token) {
      throw new Error('認証トークンがありません');
    }

    const response = await fetch(`${process.env.NEXT_PUBLIC_API_URL}/articles/${articleId}/export-headings-with-info/`, {
      method: 'GET',
      headers: {
        'Authorization': `Bearer ${token}`,
      },
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.error || 'エクスポートに失敗しました');
    }

    // Content-Dispositionヘッダーからファイル名を取得
    const contentDisposition = response.headers.get('content-disposition');

    // Base64エンコードされたヘッダーをデコード
    const decodedDisposition = contentDisposition
      ?.replace(/=\?utf-8\?b\?(.*?)\?=/gi, (match: string, p1: string) => {
        try {
          return atob(p1);
        } catch {
          return match;
        }
      });

    const filenameMatch = decodedDisposition?.match(/filename\*=UTF-8''([^;]+)/i);
    const fallbackMatch = decodedDisposition?.match(/filename="([^"]+)"/);

    let filename;
    if (filenameMatch?.[1]) {
      filename = decodeURIComponent(filenameMatch[1]);
    } else if (fallbackMatch?.[1]) {
      filename = fallbackMatch[1];
    } else {
      filename = `headings_with_info_${articleId}.docx`;
    }

    const blob = await response.blob();
    return {
      blob,
      filename
    };
  } catch (error) {
    throw handleError(error, 'REQUEST_FAILED');
  }
};

// 記事の基本情報を取得
export const getArticleBasicInfo = async (articleId: string): Promise<ApiResponse<ArticleBasicInfo>> => {
  try {
    const response = await api.get(`/article/basic-info/${articleId}/`);
    return { success: true, data: response.data.data };
  } catch (error) {
    return handleError<ArticleBasicInfo>(error, 'REQUEST_FAILED');
  }
};

// サムネイル画像のアップロード
export const uploadThumbnail = async (articleId: string, blob: Blob): Promise<ApiResponse> => {
  try {
    const formData = new FormData();
    formData.append('thumbnail', blob, 'thumbnail.jpg');

    const response = await api.post(
      `/articles/${articleId}/thumbnail/`,
      formData,
      {
        headers: {
          'Content-Type': 'multipart/form-data',
        }
      }
    );
    return { success: true, data: response.data };
  } catch (error) {
    return handleError(error, 'REQUEST_FAILED');
  }
};

// 画像プロンプトの取得
export const getImagePrompt = async (articleId: string, headingId: string): Promise<ApiResponse<{ prompt: string; heading_id: string }>> => {
  try {
    const response = await api.get(`/article/images/${articleId}/${headingId}/prompt/`);
    return { success: true, data: response.data };
  } catch (error) {
    return handleError<{ prompt: string; heading_id: string }>(error, 'REQUEST_FAILED');
  }
};

// 画像の再生成
export const regenerateImage = async (articleId: string, headingId: string, prompt: string): Promise<ApiResponse> => {
  try {
    const response = await api.post(`/article/images/${articleId}/${headingId}/regenerate/`, {
      prompt
    });
    return { success: true, data: response.data };
  } catch (error) {
    return handleError(error, 'REQUEST_FAILED');
  }
};

// テイスト再指定での画像再生成
export const regenerateImageWithStyle = async (articleId: string, headingId: string, imagePolicyId: number): Promise<ApiResponse> => {
  try {
    const response = await api.post(`/article/images/${articleId}/${headingId}/regenerate-with-style/`, {
      image_policy_id: imagePolicyId
    });
    return { success: true, data: response.data };
  } catch (error) {
    return handleError(error, 'REQUEST_FAILED');
  }
};

// 記事を見出し編集モードに準備
export const prepareArticleForHeadingEdit = async (articleId: number): Promise<ApiResponse> => {
  try {
    const response = await api.post(`/articles/prepare-heading-edit/${articleId}/`, {}, {
      headers: {
        'Authorization': `Bearer ${getAccessToken()}`
      }
    });
    return { success: true, message: response.data.message };
  } catch (error) {
    if (axios.isAxiosError(error) && error.response) {
      return handleError(error, 'REQUEST_FAILED');
    }
    return handleError(error, 'UNKNOWN_ERROR');
  }
};

/**
 * 複数の記事を一括でエクスポート
 *
 * @param articleIds - エクスポートする記事IDの配列
 * @param format - エクスポート形式 ('word' | 'markdown' | 'html')
 * @returns ZIPファイルのBlobとファイル名
 */
export const bulkExportArticles = async (
  articleIds: number[],
  format: 'word' | 'markdown' | 'html'
): Promise<{ blob: Blob; filename: string }> => {
  try {
    const token = getAccessToken();
    if (!token) {
      throw new Error('認証トークンがありません');
    }

    const response = await fetch(`${process.env.NEXT_PUBLIC_API_URL}/articles/bulk-export/`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        article_ids: articleIds,
        format: format,
      }),
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.error || '一括エクスポートに失敗しました');
    }

    // Content-Dispositionヘッダーからファイル名を取得
    const contentDisposition = response.headers.get('content-disposition');
    const decodedDisposition = contentDisposition
      ?.replace(/=\?utf-8\?b\?(.*?)\?=/gi, (match, p1) => {
        try {
          return atob(p1);
        } catch {
          return match;
        }
      });

    const filenameMatch = decodedDisposition?.match(/filename\*=UTF-8''([^;]+)/i);
    const fallbackMatch = decodedDisposition?.match(/filename="([^"]+)"/);

    let filename;
    if (filenameMatch?.[1]) {
      filename = decodeURIComponent(filenameMatch[1]);
    } else if (fallbackMatch?.[1]) {
      filename = fallbackMatch[1];
    } else {
      filename = `writely_一括ダウンロード.zip`;
    }

    const blob = await response.blob();
    return { blob, filename };
  } catch (error) {
    console.error('Bulk export error:', error);
    throw error;
  }
};

// ========================================
// 記事インポート関連
// ========================================

/**
 * 記事をインポート（HTML or Word）
 *
 * @param data - インポートデータ
 * @returns インポート結果
 */
export const importArticle = async (data: {
  import_type: 'html' | 'word';
  html_content?: string;
  file?: File;
  title?: string;
  seo_keyword?: string;
}): Promise<ApiResponse<{
  article_id: number;
  title: string | null;
  heading_count: number;
}>> => {
  try {
    const formData = new FormData();
    formData.append('import_type', data.import_type);

    if (data.html_content) {
      formData.append('html_content', data.html_content);
    }
    if (data.file) {
      formData.append('file', data.file);
    }
    if (data.title) {
      formData.append('title', data.title);
    }
    if (data.seo_keyword) {
      formData.append('seo_keyword', data.seo_keyword);
    }

    const response = await api.post('/articles/import/', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });

    return { success: true, data: response.data };
  } catch (error) {
    return handleError(error, 'VALIDATION_ERROR');
  }
};

/**
 * インポートプレビュー用のパース結果
 */
export interface ImportPreviewHeading {
  level: number;  // 0=H2, 1=H3, 2=H4
  text: string;
  content: string;
  parent_index: number | null;
}

export interface ImportPreviewData {
  title: string | null;
  lead_content: string | null;
  headings: ImportPreviewHeading[];
}

/**
 * 記事インポートのプレビュー（パースのみ、DB保存なし）
 *
 * @param data - インポートデータ
 * @returns プレビューデータ
 */
export const previewImport = async (data: {
  import_type: 'html' | 'word';
  html_content?: string;
  file?: File;
}): Promise<ApiResponse<ImportPreviewData>> => {
  try {
    const formData = new FormData();
    formData.append('import_type', data.import_type);

    if (data.html_content) {
      formData.append('html_content', data.html_content);
    }
    if (data.file) {
      formData.append('file', data.file);
    }

    const response = await api.post('/articles/import/preview/', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });

    return { success: true, data: response.data.data };
  } catch (error) {
    return handleError(error, 'VALIDATION_ERROR');
  }
};

/**
 * プレビュー確認後のインポート確定
 *
 * @param data - 確認済みインポートデータ
 * @returns 作成された記事情報
 */
export const confirmImport = async (data: {
  title: string;
  lead_content: string;
  headings: ImportPreviewHeading[];
  seo_keyword?: string;
  import_source: 'html' | 'word';
}): Promise<ApiResponse<{
  article_id: number;
  title: string | null;
  heading_count: number;
}>> => {
  try {
    const response = await api.post('/articles/import/confirm/', data, {
      timeout: 120000,
    });
    return { success: true, data: response.data };
  } catch (error) {
    return handleError(error, 'VALIDATION_ERROR');
  }
};

// ========================================
// レギュレーションチェック関連
// ========================================

/**
 * レギュレーションチェックを実行
 *
 * @param articleId - 記事ID
 * @param modelId - 使用するAIモデルID（オプション）
 * @returns チェック開始の結果
 */
export const runRegulationCheck = async (
  articleId: number,
  modelId?: number
): Promise<ApiResponse> => {
  try {
    const response = await api.post(
      `/articles/regulation-check/${articleId}/`,
      { model_id: modelId },
      {
        headers: {
          'Authorization': `Bearer ${getAccessToken()}`,
        },
      }
    );
    return { success: true, message: response.data.message };
  } catch (error) {
    return handleError(error, 'REQUEST_FAILED');
  }
};

/**
 * 特定の見出しのレギュレーションチェックを実行
 *
 * @param articleId - 記事ID
 * @param headingId - 見出しID
 * @param modelId - 使用するAIモデルID（オプション）
 * @returns チェック開始の結果
 */
export const runHeadingRegulationCheck = async (
  articleId: number,
  headingId: number,
  modelId?: number
): Promise<ApiResponse> => {
  try {
    const response = await api.post(
      `/articles/regulation-check-heading/${articleId}/${headingId}/`,
      { model_id: modelId },
      {
        headers: {
          'Authorization': `Bearer ${getAccessToken()}`,
        },
      }
    );
    return { success: true, message: response.data.message };
  } catch (error) {
    return handleError(error, 'REQUEST_FAILED');
  }
};

/**
 * 統合チェック（ファクトチェック + レギュレーションチェック）を実行
 *
 * @param articleId - 記事ID
 * @param regulationModelId - レギュレーションチェックに使用するAIモデルID（オプション）
 * @returns チェック開始の結果
 */
export const runCombinedCheck = async (
  articleId: number,
  regulationModelId?: number
): Promise<ApiResponse> => {
  try {
    const response = await api.post(
      `/articles/combined-check/${articleId}/`,
      { regulation_model_id: regulationModelId },
      {
        headers: {
          'Authorization': `Bearer ${getAccessToken()}`,
        },
      }
    );
    return { success: true, message: response.data.message };
  } catch (error) {
    return handleError(error, 'REQUEST_FAILED');
  }
};

// ========================================
// レギュレーション管理
// ========================================

import { RegulationCheckItem } from '@/types/article';

/**
 * レギュレーションチェック項目一覧を取得
 */
export const getRegulationCheckItems = async (): Promise<ApiResponse<RegulationCheckItem[]>> => {
  try {
    const response = await api.get('/regulation-check-items/', {
      headers: {
        'Authorization': `Bearer ${getAccessToken()}`,
      },
    });
    return { success: true, data: response.data };
  } catch (error) {
    return handleError<RegulationCheckItem[]>(error, 'REQUEST_FAILED');
  }
};

/**
 * レギュレーションチェック項目を作成
 */
export const createRegulationCheckItem = async (data: {
  check_name: string;
  ng_examples?: string;
  ok_criteria?: string;
  reference_material?: string;
  category?: string;
  is_active?: boolean;
}): Promise<ApiResponse<RegulationCheckItem>> => {
  try {
    const response = await api.post('/regulation-check-items/', data, {
      headers: {
        'Authorization': `Bearer ${getAccessToken()}`,
        'Content-Type': 'application/json',
      },
    });
    return { success: true, data: response.data };
  } catch (error) {
    return handleError<RegulationCheckItem>(error, 'VALIDATION_ERROR');
  }
};

/**
 * レギュレーションチェック項目を更新
 */
export const updateRegulationCheckItem = async (
  itemId: number,
  data: Partial<{
    check_name: string;
    ng_examples: string;
    ok_criteria: string;
    reference_material: string;
    category: string;
    is_active: boolean;
  }>
): Promise<ApiResponse<RegulationCheckItem>> => {
  try {
    const response = await api.patch(`/regulation-check-items/${itemId}/`, data, {
      headers: {
        'Authorization': `Bearer ${getAccessToken()}`,
        'Content-Type': 'application/json',
      },
    });
    return { success: true, data: response.data };
  } catch (error) {
    return handleError<RegulationCheckItem>(error, 'VALIDATION_ERROR');
  }
};

/**
 * レギュレーションチェック項目を削除
 */
export const deleteRegulationCheckItem = async (itemId: number): Promise<ApiResponse> => {
  try {
    await api.delete(`/regulation-check-items/${itemId}/`, {
      headers: {
        'Authorization': `Bearer ${getAccessToken()}`,
      },
    });
    return { success: true };
  } catch (error) {
    return handleError(error, 'REQUEST_FAILED');
  }
};

/**
 * フォームマスターデータ統合API
 * トーン、見出しポリシー、ターゲットオーディエンス、画像ポリシーを
 * 1回のリクエストで取得（DB接続数削減のため）
 */
export interface FormMasterData {
  tones: Array<{ id: number; tone_name: string; status: boolean }>;
  heading_policies: Array<{ id: number; policy_name: string; status: boolean }>;
  target_audiences: Array<{ id: number; audience_name: string; status: boolean }>;
  image_policies: Array<{ id: number; image_name: string; status: boolean; is_thumbnail_style: boolean; is_overlay_image: boolean }>;
  thumbnail_image_policies: Array<{ id: number; image_name: string; status: boolean; is_thumbnail_style: boolean; is_overlay_image: boolean }>;
  article_settings?: {
    id: number;
    seo_keyword: string;
    industry: string;
    site_url: string;
    service: string;
    article_purpose: string;
    writer_request: string;
    tone: number;
    heading_policy: number;
    target_audience: number;
    image_policy?: number;
    thumbnail_image_policy?: number;
    is_heading_check: boolean;
    is_site_introduction: boolean;
    is_image_generation: boolean;
    is_web_search: boolean;
  };
}

export const getFormMasterData = async (articleId?: string): Promise<ApiResponse<FormMasterData>> => {
  try {
    const url = articleId ? `/form-master-data/${articleId}/` : '/form-master-data/';
    const response = await api.get(url, {
      headers: {
        'Authorization': `Bearer ${getAccessToken()}`,
      },
    });
    return {
      success: true,
      data: response.data.data,
    };
  } catch (error) {
    return handleError<FormMasterData>(error, 'FETCH_ERROR');
  }
};

/**
 * 記事を一括作成する
 */
export const bulkCreateArticles = async (articles: BulkArticleRow[]): Promise<BulkArticleCreateResponse> => {
  try {
    const response = await api.post(
      '/article/bulk-basic-settings/',
      { articles },
      {
        headers: {
          'Authorization': `Bearer ${getAccessToken()}`,
          'Content-Type': 'application/json',
        },
      }
    );
    return response.data;
  } catch (error) {
    if (axios.isAxiosError(error) && error.response?.data) {
      return error.response.data as BulkArticleCreateResponse;
    }
    return { success: false, message: '一括入稿中にエラーが発生しました' };
  }
};

// ========================================
// バージョン管理 API
// ========================================

export const getArticleVersions = async (articleId: string): Promise<ArticleVersionSummary[]> => {
  const response = await api.get(`/articles/${articleId}/versions/`, {
    headers: { 'Authorization': `Bearer ${getAccessToken()}` },
  });
  return response.data;
};

export const createManualVersion = async (articleId: string): Promise<ArticleVersionContent> => {
  const response = await api.post(
    `/articles/${articleId}/versions/`,
    {},
    { headers: { 'Authorization': `Bearer ${getAccessToken()}` } },
  );
  return response.data;
};

export const getArticleVersion = async (articleId: string, versionId: number): Promise<ArticleVersionContent> => {
  const response = await api.get(`/articles/${articleId}/versions/${versionId}/`, {
    headers: { 'Authorization': `Bearer ${getAccessToken()}` },
  });
  return response.data;
};

/**
 * 記事の音声データ生成を開始する
 *
 * @param articleId 記事ID
 * @param voiceName 使用するボイス名
 * @returns 生成開始結果
 */
export const generateArticleAudio = async (
  articleId: string,
  voiceName: string = 'Kore',
  audioType: 'reading' | 'podcast' = 'reading',
  hostVoice?: string,
  guestVoice?: string,
): Promise<ApiResponse> => {
  try {
    const response = await api.post(
      `/articles/${articleId}/generate-audio/`,
      { voice_name: voiceName, audio_type: audioType, host_voice: hostVoice, guest_voice: guestVoice },
      { headers: { 'Authorization': `Bearer ${getAccessToken()}`, 'Content-Type': 'application/json' } },
    );
    return { success: true, message: response.data.message };
  } catch (error) {
    return handleError(error, 'REQUEST_FAILED');
  }
};

/**
 * 記事の音声ファイルをダウンロードする
 *
 * @param articleId 記事ID
 * @returns Blobデータ
 */
export const downloadArticleAudio = async (articleId: string): Promise<Blob | null> => {
  try {
    const response = await api.get(`/articles/${articleId}/audio/`, {
      headers: { 'Authorization': `Bearer ${getAccessToken()}` },
      responseType: 'blob',
    });
    return response.data;
  } catch (error) {
    console.error('音声ダウンロードエラー:', error);
    return null;
  }
};

export const fetchPodcastScript = async (articleId: string): Promise<{ script: string; host_voice: string; guest_voice: string } | null> => {
  try {
    const response = await api.get(`/articles/${articleId}/podcast-script/`, {
      headers: { 'Authorization': `Bearer ${getAccessToken()}` },
    });
    return response.data;
  } catch {
    return null;
  }
};

export const fetchVoiceSample = async (voiceName: string): Promise<Blob | null> => {
  try {
    const response = await api.get(`/voice-sample/${voiceName}/`, {
      headers: { 'Authorization': `Bearer ${getAccessToken()}` },
      responseType: 'blob',
      timeout: 30000,
    });
    return response.data;
  } catch (error) {
    console.error(`ボイスサンプル取得エラー (${voiceName}):`, error);
    return null;
  }
};
